import { LeaveConfirmComponent } from 'src/app/@shared/components/leave-confirm/leave-confirm.component'
import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  signal
} from '@angular/core'
import {
  NavigationEnd,
  NavigationStart,
  Router,
  RouterOutlet
} from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { layoutSizes } from './@core/layout/model/layout.model'
import { LayoutRepository } from './@core/layout/state/layout.repository'

import { AuthService } from './@core/auth/auth.service'
import { ClarityService } from './@core/services/clarity/clarity.service'
import { MobileIntegrationService } from './@core/services/mobile-integration/mobile-integration.service'
import { PlatformDetectorService } from './@core/services/platform-detector/platform-detector.service'
import { TagManagerService } from './@core/services/tag-manager/tag-manager.service'
import { AwsRumService } from './@core/services/aws-rum/aws-rum.service'
import { LoadingComponent } from './@shared/components/loading/loading.component'
import { OfflineIndicatorComponent } from './@shared/components/offline-indicator/offline-indicator.component'
import { ToastComponent } from './@theme/components/toast/toast.component'
import { ThemeRepository } from './@theme/state/theme.repository'
import { DashboardRepository } from './features/dashboard/state/dashboard.repository'
import { RedirectModalRepository } from './@shared/components/redirect-modal/state/redirect-modal.repository'
import { RedirectModalComponent } from './@shared/components/redirect-modal/redirect-modal.component'
import { BrowserEmptyStateComponent } from './@shared/components/browser-empty-state/browser-empty-state.component'
import { ZardLoaderComponent } from '@/shared/components/loader'

@Component({
  selector: 'app-root',
  standalone: true,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ZardLoaderComponent,
    CommonModule,
    RouterOutlet,
    ToastComponent,
    LeaveConfirmComponent,
    OfflineIndicatorComponent,
    LoadingComponent,
    RedirectModalComponent,
    BrowserEmptyStateComponent
  ]
})
export class AppComponent {
  isOnline = navigator.onLine
  token = signal('')
  isLoading = signal(false)
  showRedirectModal = signal(false)

  showInAppBrowserError = signal(false)

  @HostListener('window:resize')
  onResize() {
    this.layoutRepository.setIsMobile(window.innerWidth < layoutSizes.mobile)
    this.layoutRepository.setIsMdScreen(window.innerWidth < layoutSizes.md)
  }

  constructor(
    private readonly translate: TranslateService,
    private readonly themeRepository: ThemeRepository,
    private layoutRepository: LayoutRepository,
    private router: Router,
    private tagManagerService: TagManagerService,
    private clarityService: ClarityService,
    private dashboardRepository: DashboardRepository,
    private platformDetector: PlatformDetectorService,
    private mobileIntegration: MobileIntegrationService,
    private authService: AuthService,
    private redirectModalRepository: RedirectModalRepository,
    private awsRumService: AwsRumService
  ) {
    this.initLanguage()
    this.tagManagerService.initGoogleTagManager()
    this.clarityService.initClarity()
    this.initMobile()
    this.awsRumService.initAwsRum()

    this.mobileIntegration.handleMobileRedirect()
  }

  async ngOnInit(): Promise<void> {
    await this.authService.initializeAuth()

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        document.body.style.overflow = ''
        document.body.style.position = ''
        document.body.style.width = ''
      }

      if (event instanceof NavigationEnd) {
        this.mobileIntegration.notifyNavigation(event.url)
        if (event.url.startsWith('/auth')) {
          const isInAppBrowser = this.platformDetector.isInAppBrowser()

          if (isInAppBrowser && !this.platformDetector.isInAppWebView()) {
            this.showInAppBrowserError.set(true)
          }
        }
      }
    })

    this.dashboardRepository.isLoading$.subscribe(isLoading => {
      this.isLoading.set(isLoading)
    })

    this.redirectModalRepository.showModal$.subscribe(showRedirectModal => {
      this.showRedirectModal.set(showRedirectModal)
    })
  }

  initMobile() {
    if (!this.platformDetector.isInAppWebView()) return

    this.mobileIntegration.initMobile()
  }

  initLanguage(): void {
    const defaultLanguage = 'pt-BR'

    this.translate.setDefaultLang(defaultLanguage)

    const browserLang = this.translate.getBrowserCultureLang()

    this.translate.use(browserLang || defaultLanguage)

    // handle when fallback is applied
    this.translate.onDefaultLangChange.subscribe(event => {
      this.translate.use(event.lang)
    })
  }
}
